import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { cx } from './cx';

type ShowToast = (message: string) => void;

const ToastContext = createContext<ShowToast | null>(null);

/** Confirmation temporaire en bas d'écran (une à la fois, 2,6 s). */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const timer = useRef<number>();

  const show = useCallback<ShowToast>((text) => {
    window.clearTimeout(timer.current);
    setMessage(text);
    setVisible(true);
    timer.current = window.setTimeout(() => setVisible(false), 2600);
  }, []);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <ToastContext.Provider value={show}>
      {children}
      <div
        role="status"
        aria-live="polite"
        className={cx(
          'pointer-events-none fixed bottom-7 left-1/2 z-[100] flex max-w-[calc(100vw-32px)] -translate-x-1/2 items-center gap-2.5 rounded-pill bg-heading py-3 pl-3.5 pr-[18px] text-[13.5px] text-white shadow-toast transition-all duration-300 ease-enter',
          visible ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0',
        )}
      >
        {message && (
          <>
            <CheckCircle2 size={18} className="shrink-0 text-emerald-400" aria-hidden />
            {message}
          </>
        )}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ShowToast {
  const show = useContext(ToastContext);
  if (!show) throw new Error('useToast doit être utilisé dans un <ToastProvider>.');
  return show;
}
